import React, { useState } from 'react';
import {JsonToSwiftSampleData} from '../../../../../DummyData/ConvertSampleJson';
// Define types for our component
interface JsonToSwiftProps {}

// Swift conversion options
type SwiftOption = 'struct' | 'class';
type AccessLevel = 'internal' | 'public' | 'private' | 'fileprivate';
type NamingConvention = 'camelCase' | 'snake_case' | 'PascalCase';

const JsonToSwift: React.FC<JsonToSwiftProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Swift output
  const [swiftOutput, setSwiftOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the root class/struct name
  const [rootName, setRootName] = useState<string>('RootModel');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for Swift type (struct or class)
  const [swiftType, setSwiftType] = useState<SwiftOption>('struct');
  // State for access level
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('internal');
  // State for property naming convention
  const [namingConvention, setNamingConvention] = useState<NamingConvention>('camelCase');
  // State to track if the conversion happened
  const [isSwiftGenerated, setIsSwiftGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for Swift model preview
  const [showPreview, setShowPreview] = useState<boolean>(true);
  // State for Codable conformance
  const [conformToCodable, setConformToCodable] = useState<boolean>(true);
  // State for including CodingKeys
  const [includeCodingKeys, setIncludeCodingKeys] = useState<boolean>(true);
  // State for using optionals for all properties
  const [useOptionals, setUseOptionals] = useState<boolean>(true);
  
  // Sample JSON data


  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsSwiftGenerated(false);
  };

  // Handle root name change
  const handleRootNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRootName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(JsonToSwiftSampleData, null, 2));
    setError('');
    setIsSwiftGenerated(false);
    setSwiftOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setSwiftOutput('');
    setError('');
    setRootName('RootModel');
    setSwiftType('struct');
    setAccessLevel('internal');
    setNamingConvention('camelCase');
    setConformToCodable(true);
    setIncludeCodingKeys(true);
    setUseOptionals(true);
    setIsSwiftGenerated(false);
  };

  // Convert JSON key based on naming convention
  const formatPropertyName = (key: string): string => {
    // Remove special characters and prepare for conversion
    let formattedKey = key.replace(/[^a-zA-Z0-9_]/g, '');
    
    // Handle keys that start with a number
    if (/^\d/.test(formattedKey)) {
      formattedKey = 'n' + formattedKey;
    }
    
    // Apply naming convention
    switch (namingConvention) {
      case 'camelCase':
        return formattedKey.replace(/(_\w)/g, (match) => match[1].toUpperCase())
          .replace(/^[A-Z]/, (match) => match.toLowerCase());
        
      case 'snake_case':
        return formattedKey.replace(/([A-Z])/g, '_$1').toLowerCase()
          .replace(/^_/, '');
        
      case 'PascalCase':
        return formattedKey.charAt(0).toUpperCase() +
          formattedKey.slice(1).replace(/(_\w)/g, (match) => match[1].toUpperCase());
        
      default:
        return formattedKey;
    }
  };

  // Convert a JSON type to Swift type
  const getSwiftType = (value: any): string => {
    if (value === null) {
      return useOptionals ? 'Any?' : 'Any';
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return useOptionals ? '[Any]?' : '[Any]';
      }
      
      // Check if all items are of the same type
      const firstType = typeof value[0];
      const allSameType = value.every(item => typeof item === firstType);
      
      if (allSameType) {
        if (typeof value[0] === 'object' && value[0] !== null) {
          // For arrays of objects, we'll create a new model
          return useOptionals ? `[${formatClassName(getModelNameFromKey("item"))}]?` : `[${formatClassName(getModelNameFromKey("item"))}]`;
        } else {
          return useOptionals ? `[${getSwiftType(value[0])}]?` : `[${getSwiftType(value[0])}]`;
        }
      } else {
        return useOptionals ? '[Any]?' : '[Any]';
      }
    }
    
    switch (typeof value) {
      case 'string':
        return useOptionals ? 'String?' : 'String';
      case 'number':
        // Check if it's an integer or a double
        return Number.isInteger(value) 
          ? (useOptionals ? 'Int?' : 'Int') 
          : (useOptionals ? 'Double?' : 'Double');
      case 'boolean':
        return useOptionals ? 'Bool?' : 'Bool';
      case 'object':
        // For nested objects, create a new model
        return useOptionals ? `${formatClassName(getModelNameFromKey("nestedObject"))}?` : formatClassName(getModelNameFromKey("nestedObject"));
      default:
        return useOptionals ? 'Any?' : 'Any';
    }
  };

  // Format class/struct name to PascalCase
  const formatClassName = (name: string): string => {
    // Remove special characters
    let formattedName = name.replace(/[^a-zA-Z0-9_]/g, '');
    
    // Handle names that start with a number
    if (/^\d/.test(formattedName)) {
      formattedName = 'N' + formattedName;
    }
    
    // Convert to PascalCase
    return formattedName.charAt(0).toUpperCase() + 
      formattedName.slice(1).replace(/(_\w)/g, (match) => match[1].toUpperCase());
  };

  // Get model name from key
  const getModelNameFromKey = (key: string): string => {
    // Handle plural keys - convert to singular for model name
    const singularKey = key.endsWith('s') ? key.slice(0, -1) : key;
    return formatClassName(singularKey);
  };

  // Build Swift models from a JSON object
  const buildSwiftModels = (
    obj: any, 
    modelName: string, 
    visitedModels = new Map<string, string>()
  ): Map<string, string> => {
    if (typeof obj !== 'object' || obj === null) {
      return visitedModels;
    }
    
    // Prepare properties, coding keys, and nested models
    let properties: string[] = [];
    let codingKeys: string[] = [];
    
    Object.entries(obj).forEach(([key, value]) => {
      const swiftKey = formatPropertyName(key);
      const originalKey = key;
      
      // Add to coding keys if the swift key is different from original key
      if (includeCodingKeys && swiftKey !== originalKey) {
        codingKeys.push(`    case ${swiftKey} = "${originalKey}"`);
      }
      
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        // For arrays of objects
        const itemModelName = formatClassName(getModelNameFromKey(key) + "Item");
        properties.push(`    ${accessLevel} ${useOptionals ? 'var' : 'let'} ${swiftKey}: [${itemModelName}]${useOptionals ? '?' : ''}`);
        
        // Recursively build model for array items
        if (!visitedModels.has(itemModelName) && value[0] !== null) {
          buildSwiftModels(value[0], itemModelName, visitedModels);
        }
      } else if (typeof value === 'object' && value !== null) {
        // For nested objects
        const nestedModelName = formatClassName(getModelNameFromKey(key));
        properties.push(`    ${accessLevel} ${useOptionals ? 'var' : 'let'} ${swiftKey}: ${nestedModelName}${useOptionals ? '?' : ''}`);
        
        // Recursively build model for nested object
        if (!visitedModels.has(nestedModelName)) {
          buildSwiftModels(value, nestedModelName, visitedModels);
        }
      } else {
        // For primitive types
        properties.push(`    ${accessLevel} ${useOptionals ? 'var' : 'let'} ${swiftKey}: ${getSwiftType(value)}`);
      }
    });
    
    // Build the model
    let modelCode = `${accessLevel} ${swiftType} ${modelName}`;
    
    // Add Codable conformance if selected
    if (conformToCodable) {
      modelCode += `: Codable`;
    }
    
    modelCode += ` {\n`;
    
    // Add properties
    if (properties.length > 0) {
      modelCode += properties.join('\n') + '\n';
    }
    
    // Add CodingKeys if needed
    if (conformToCodable && includeCodingKeys && codingKeys.length > 0) {
      modelCode += '\n    private enum CodingKeys: String, CodingKey {\n';
      modelCode += codingKeys.join('\n');
      modelCode += '\n    }\n';
    }
    
    modelCode += `}\n`;
    
    // Add this model to our collection
    visitedModels.set(modelName, modelCode);
    
    return visitedModels;
  };

  // Generate Swift code from JSON
  const generateSwiftModels = (jsonData: any, rootModelName: string): string => {
    const visitedModels = buildSwiftModels(jsonData, rootModelName);
    
    // Arrange models with root model last
    let modelStrings: string[] = [];
    
    visitedModels.forEach((modelCode, modelName) => {
      if (modelName !== rootModelName) {
        modelStrings.push(modelCode);
      }
    });
    
    // Add root model at the end
    if (visitedModels.has(rootModelName)) {
      modelStrings.push(visitedModels.get(rootModelName)!);
    }
    
    return modelStrings.join('\n');
  };

  // Convert JSON to Swift
  const convertJsonToSwift = () => {
    setError('');
    setIsLoading(true);
    setIsSwiftGenerated(false);
    setSwiftOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      const formattedRootName = formatClassName(rootName);
      
      const swiftCode = generateSwiftModels(jsonData, formattedRootName);
      
      // Add import Foundation at the top
      const finalCode = `import Foundation\n\n${swiftCode}`;
      
      setSwiftOutput(finalCode);
      setIsSwiftGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Swift: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Swift to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(swiftOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Swift file
  const downloadSwift = () => {
    const blob = new Blob([swiftOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${rootName || 'Model'}.swift`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Swift Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Swift models. Paste your JSON data below to generate Swift structs or classes that conform to Codable.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="rootName" className="block text-sm font-medium text-gray-700 mb-1">
                Root Model Name
              </label>
              <input
                type="text"
                id="rootName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={rootName}
                onChange={handleRootNameChange}
                placeholder="Enter root model name"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="swiftType" className="block text-sm font-medium text-gray-700 mb-1">
                    Swift Type
                  </label>
                  <select
                    id="swiftType"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={swiftType}
                    onChange={(e) => setSwiftType(e.target.value as SwiftOption)}
                  >
                    <option value="struct">Struct</option>
                    <option value="class">Class</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="accessLevel" className="block text-sm font-medium text-gray-700 mb-1">
                    Access Level
                  </label>
                  <select
                    id="accessLevel"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={accessLevel}
                    onChange={(e) => setAccessLevel(e.target.value as AccessLevel)}
                  >
                    <option value="internal">Internal (default)</option>
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="fileprivate">Fileprivate</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="namingConvention" className="block text-sm font-medium text-gray-700 mb-1">
                    Property Naming Convention
                  </label>
                  <select
                    id="namingConvention"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={namingConvention}
                    onChange={(e) => setNamingConvention(e.target.value as NamingConvention)}
                  >
                    <option value="camelCase">camelCase (Swift standard)</option>
                    <option value="snake_case">snake_case</option>
                    <option value="PascalCase">PascalCase</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="conformToCodable"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={conformToCodable}
                    onChange={(e) => setConformToCodable(e.target.checked)}
                  />
                  <label htmlFor="conformToCodable" className="ml-2 block text-sm text-gray-700">
                    Conform to Codable protocol
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeCodingKeys"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={includeCodingKeys}
                    onChange={(e) => setIncludeCodingKeys(e.target.checked)}
                    disabled={!conformToCodable}
                  />
                  <label 
                    htmlFor="includeCodingKeys" 
                    className={`ml-2 block text-sm ${!conformToCodable ? 'text-gray-400' : 'text-gray-700'}`}
                  >
                    Include CodingKeys for different JSON keys
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useOptionals"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={useOptionals}
                    onChange={(e) => setUseOptionals(e.target.checked)}
                  />
                  <label htmlFor="useOptionals" className="ml-2 block text-sm text-gray-700">
                    Use optional types (var property?)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPreview"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                  />
                  <label htmlFor="showPreview" className="ml-2 block text-sm text-gray-700">
                    Show Preview
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToSwift}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Swift'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Swift Output
            </label>
            
            {isSwiftGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Swift'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadSwift}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .swift
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isSwiftGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Swift models will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isSwiftGenerated && (
              <div className="flex-grow flex flex-col">
                {showPreview ? (
                  <div className="mb-3">
                    <div className="mb-2 flex justify-between items-center">
                      <h3 className="font-medium text-gray-800">Preview:</h3>
                      <button
                        className="text-xs text-gray-600 hover:text-gray-800"
                        onClick={() => setShowPreview(false)}
                      >
                        Hide Preview
                      </button>
                    </div>
                    <div className="border border-gray-200 rounded-md h-48 overflow-auto p-3 mb-3 bg-gray-50 font-mono text-xs">
                      <pre>{swiftOutput}</pre>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={() => setShowPreview(true)}
                    >
                      Show Preview
                    </button>
                  </div>
                )}
                
                <div className="flex-grow flex flex-col">
                  <label htmlFor="swiftOutput" className="text-sm font-medium text-gray-700 mb-1">
                    Swift Code:
                  </label>
                  <textarea
                    id="swiftOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                    value={swiftOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isSwiftGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Swift Models Generated Successfully!</p>
              </div>
              <p className="text-sm">
                {conformToCodable ? 
                  "The generated Swift models conform to the Codable protocol for easy JSON serialization and deserialization." :
                  "You can add protocol conformance as needed for these Swift models."
                }
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Swift Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into Swift structs or classes. It analyzes your JSON structure
              and generates proper Swift types with appropriate property names and type definitions.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Swift Type Generation</strong> - Create structs or classes based on your JSON structure</li>
              <li><strong>Codable Support</strong> - Add Codable protocol conformance for easy serialization</li>
              <li><strong>Nested Models</strong> - Automatically creates models for nested objects</li>
              <li><strong>Property Customization</strong> - Choose between camelCase, snake_case, or PascalCase naming</li>
              <li><strong>Access Level Control</strong> - Set internal, public, private, or fileprivate access levels</li>
              <li><strong>Optional Types</strong> - Generate properties as optionals to handle missing data</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>For Swift best practices, keep the "camelCase" naming convention (Swift standard)</li>
              <li>Use "struct" for immutable data models and "class" when inheritance or reference semantics are needed</li>
              <li>The "Use optional types" option helps handle missing JSON fields but may require unwrapping in your code</li>
              <li>For JSON with inconsistent types, you may need to manually adjust the generated models</li>
              <li>Always review generated code for edge cases or special requirements in your project</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    );
}

export default JsonToSwift;